import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../../firebase";
import { 
  Box, Typography, Grid, Paper, CircularProgress, Stack, Chip, 
  Divider, TextField, Card, CardContent, Avatar, 
  IconButton, Button, Container
} from "@mui/material";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js";

// Icons
import { 
  Users, Truck, ShoppingCart, TrendingUp, Wallet, 
  MessageSquare, Bell, ArrowUpRight, Calendar, ChevronRight
} from "lucide-react";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats] = useState({
    sale: 125000,
    tripCount: 45,
    totalBrass: 88,
    purchase: 12,
    income: 75000
  });

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(() => {
      setTimeout(() => setLoading(false), 800);
    });
    return () => unsub();
  }, []);

  const STAT_CARDS = [
    { title: "Today's Enquiry", value: stats.sale, color: "#6366f1", icon: <TrendingUp size={20} />, unit: "₹" },
    { title: "Active Customers", value: stats.tripCount, color: "#8b5cf6", icon: <Users size={20} />, unit: "" },
    { title: "Pending Deals", value: stats.totalBrass, color: "#ec4899", icon: <ShoppingCart size={20} />, unit: "" },
    { title: "Rejected Leads", value: stats.purchase, color: "#f59e0b", icon: <Truck size={20} />, unit: "" },
    { title: "Affiliate Leads", value: stats.income, color: "#10b981", icon: <Wallet size={20} />, unit: "₹" },
  ];

  if (loading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f8fafc' }}>
      <CircularProgress thickness={4} size={45} sx={{ color: '#6366f1' }} />
    </Box>
  );

  return (
    <Box sx={{ background: "#f1f5f9", minHeight: "100vh", py: 1 }}>
      <Container maxWidth="xl">
        
        {/* Header & Filter Bar */}
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} mb={5}>
          <Box>
            <Typography variant="h4" fontWeight={800} sx={{ color: "#1e293b", letterSpacing: "-1px" }}>
              Analytics Dashboard
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>
              Real-time monitoring of your business performance
            </Typography>
          </Box>

          <Paper elevation={0} sx={{ p: 1, borderRadius: 3, border: "1px solid #e2e8f0", display: 'flex', alignItems: 'center', gap: 1, bgcolor: "rgba(255,255,255,0.8)", backdropFilter: "blur(10px)" }}>
            <Stack direction="row" spacing={1} alignItems="center" px={1}>
              <Calendar size={18} color="#64748b" />
              <TextField type="date" variant="standard" size="small" InputProps={{ disableUnderline: true }} sx={{ width: 130 }} defaultValue="2023-10-01" />
              <Typography color="#cbd5e1">—</Typography>
              <TextField type="date" variant="standard" size="small" InputProps={{ disableUnderline: true }} sx={{ width: 130 }} defaultValue="2023-10-31" />
            </Stack>
            <Button variant="contained" disableElevation sx={{ borderRadius: 2, bgcolor: "#1e293b", '&:hover': { bgcolor: "#0f172a" }, textTransform: 'none', fontWeight: 600 }}>
              Update
            </Button>
          </Paper>
        </Stack>

        {/* Stats Grid */}
        <Grid className="row" spacing={3} mb={2}>
          {STAT_CARDS.map((card, i) => (
            <Grid className="col-sm-3 mb-3" item xs={12} sm={6} md={2.4} key={i}>
              <Card sx={{ 
                borderRadius: 4, 
                border: "1px solid #e2e8f0",
                boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                position: 'relative',
                overflow: 'visible',
                transition: "0.3s",
                "&:hover": { transform: "translateY(-5px)", boxShadow: "0 12px 20px -5px rgba(0,0,0,0.05)" }
              }}>
                <Box sx={{ position: 'absolute', top: -1, left: 20, right: 20, height: "3px", background: `linear-gradient(90deg, transparent, ${card.color}, transparent)`, borderRadius: "0 0 100px 100px" }} />
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" justifyContent="space-between" mb={2}>
                    <Avatar variant="rounded" sx={{ bgcolor: `${card.color}10`, color: card.color, borderRadius: 2.5, width: 40, height: 40 }}>
                      {card.icon}
                    </Avatar>
                    <Chip label="+2.5%" size="small" sx={{ bgcolor: "#f0fdf4", color: "#16a34a", fontWeight: 700, fontSize: "0.7rem" }} />
                  </Stack>
                  <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: 'uppercase', letterSpacing: "0.5px" }}>
                    {card.title}
                  </Typography>
                  <Typography variant="h5" fontWeight={800} sx={{ color: "#1e293b", mt: 0.5 }}>
                    {card.unit}{card.value.toLocaleString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Grid className="row" spacing={4}>
          {/* Main Chart */}
          <Grid className="col-sm-12" item xs={12} md={8}>
            <Paper sx={{ p: 4, borderRadius: 5, border: "1px solid #e2e8f0", boxShadow: "none" }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
                <Box>
                  <Typography variant="h6" fontWeight={800} color="#1e293b">Revenue Flow</Typography>
                  <Typography variant="caption" color="#64748b">Weekly performance vs Last week</Typography>
                </Box>
                <IconButton><ArrowUpRight size={20} /></IconButton>
              </Stack>
              <Box sx={{ height: 320 }}>
                <Bar 
                  data={{
                    labels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
                    datasets: [{
                      label: 'Current Period',
                      data: [45, 59, 80, 81, 56, 55, 40],
                      backgroundColor: '#6366f1',
                      borderRadius: 6,
                      barThickness: 15
                    }]
                  }}
                  options={{ 
                    maintainAspectRatio: false, 
                    plugins: { legend: { display: false } },
                    scales: { y: { grid: { display: false } }, x: { grid: { display: false } } }
                  }} 
                />
              </Box>
            </Paper>
          </Grid>

        </Grid>
      </Container>
    </Box>
  );
};

export default Dashboard;